import * as React from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';

import {
  ajusterNotation,
  enregistrerAjustementRecup,
  libelleAjustement,
  RECUP_MIN_S,
  useAjustementRecup,
} from '../Construction/ajustementRecup.ts';
import { estCoach, nomAthlete, useMoi, type AthleteRattache } from '../Auth/moi.ts';

const EXEMPLE = "5x(5'@MP-R2')";

// Reglage de l'ajustement des recuperations (page Mes preferences) : un
// decalage en secondes applique a chaque recuperation en temps des seances.
// Regle par le coach (le sien, ou celui d'un athlete : page Athletes) ;
// l'athlete le consulte.
export default function AjustementRecupEditeur({ athlete = null }: { athlete?: AthleteRattache | null }) {
  const moi = useMoi();
  const lectureSeule = !estCoach(moi);
  const sso = athlete?.ssoUserId ?? null;
  const enregistre = useAjustementRecup(sso);
  const [valeur, setValeur] = React.useState<number>(enregistre);
  const [etat, setEtat] = React.useState<'' | 'enregistrement' | 'enregistre' | 'erreur'>('');

  React.useEffect(() => setValeur(enregistre), [enregistre]);

  const enregistrer = (v: number) => {
    setEtat('enregistrement');
    enregistrerAjustementRecup(v, sso)
      .then(() => setEtat('enregistre'))
      .catch(() => setEtat('erreur'));
  };

  return (
    <Box id="preference-recuperation" sx={{ mt: 4 }}>
      <h2>Récupérations</h2>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        {athlete
          ? <>Ajuste la durée de chaque récupération des séances de <b>{nomAthlete(athlete)}</b></>
          : lectureSeule
            ? <>Durée ajoutée ou retirée à chaque récupération de tes séances, réglée par ton coach</>
            : <>Ajuste la durée de chaque récupération de tes séances</>}{' '}
        (les « R… » exprimés en temps) : en positif pour
        récupérer plus longtemps, en négatif pour durcir les séances. La notation des séances ne change pas ;
        l'ajustement s'applique à l'affichage et aux calculs (durée, charge, RPE). Une récupération ne descend pas
        sous {RECUP_MIN_S} s ; les récupérations en distance (ex : R400m) ne sont pas modifiées.
      </Typography>

      <Box sx={{ maxWidth: 520, px: 1 }}>
        <Slider
          value={valeur}
          min={-120}
          max={120}
          step={15}
          marks={[
            { value: -120, label: '−2′' },
            { value: -60, label: '−1′' },
            { value: 0, label: '0' },
            { value: 60, label: '+1′' },
            { value: 120, label: '+2′' },
          ]}
          disabled={lectureSeule}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => libelleAjustement(v)}
          onChange={(_e, v) => {
            setValeur(v as number);
            setEtat('');
          }}
          onChangeCommitted={(_e, v) => enregistrer(v as number)}
          aria-label="Ajustement des récupérations"
        />
      </Box>

      <Typography sx={{ mt: 1 }}>
        Ajustement : <b>{libelleAjustement(valeur)}</b> par récupération — exemple : <code>{EXEMPLE}</code> devient{' '}
        <code>{ajusterNotation(EXEMPLE, valeur)}</code>
      </Typography>
      <Typography variant="caption" color={etat === 'erreur' ? 'error' : 'text.secondary'}>
        {etat === 'enregistrement' && 'Enregistrement…'}
        {etat === 'enregistre' && 'Enregistré'}
        {etat === 'erreur' && 'Enregistrement impossible'}
      </Typography>
    </Box>
  );
}
